import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, ImageOverlay, Marker, Rectangle, Polygon, Pane, useMapEvents, Polyline } from 'react-leaflet';
import { CRS, latLngBounds, icon } from 'leaflet';
import { useWindowSize } from 'react-use';
import jsonData from '../../signals/jsonData';
import appState from '../../signals/appState';
import configData from '../../signals/configData';
import translation from '../../signals/translations';
import markerFav from './assets/marker-fav.svg';
import markerStart from './assets/marker-start.svg';
import markerLeft from './assets/left.svg';
import markerRight from './assets/right.svg';
import markerUp from './assets/up.svg';
import markerDown from './assets/down.svg';
import endNav from './assets/end-nav.svg';
import FeatureWithText from './FeatureWithText';
import { findPath } from '../../tools/findPath';
import { computeMultiStopRoute } from '../../tools/computeMultiStopRoute';
import { boothExhibitorsOf, boothHasNumber, boothNumbersOf, exhibitorKeyOf, firstExhibitorAtBooth } from '../../tools/boothExhibitors';
import './leaflet.css';

const NavMarker = ({ center, marker }) => {
	const [currentZoom, setCurrentZoom] = useState();

	const mapEvents = useMapEvents({
		zoomend: () => {
			setCurrentZoom(mapEvents.getZoom());
		},
	});

	const markerSize = useMemo(() => {
		const size = currentZoom * 12;
		return [size, size];
	}, [currentZoom]);

	const markerIcon = useMemo(() => {
		return icon({
			iconUrl: marker,
			iconSize: markerSize,
		});
	}, [marker, markerSize]);

	return (
		<Marker
			position={{
				lat: center[0],
				lng: center[1],
			}}
			icon={markerIcon}
		/>
	);
};

const FavMarker = ({ bounds, marker }) => {
	const markerIcon = useMemo(() => {
		return icon({
			iconUrl: marker,
			iconSize: 20,
		});
	}, [marker]);

	const center = useMemo(() => {
		if (!bounds) return [0, 0];
		const lat = (bounds[0][0] + bounds[1][0]) / 2 + 3;
		const lng = (bounds[0][1] + bounds[1][1]) / 2;
		return [lat, lng];
	}, [bounds]);

	return (
		<Marker
			position={{
				lat: center[0],
				lng: center[1],
			}}
			icon={markerIcon}
		/>
	);
};

const MapPane = ({ bounds, currentMap, onImageLoad }) => {
	return (
		<Pane name='map' style={{ zIndex: 100 }}>
			{bounds ? <ImageOverlay url={currentMap.url} bounds={bounds} eventHandlers={{ load: onImageLoad }} /> : null}
		</Pane>
	);
};

const NavStartPane = ({ setUserNavStart }) => {
	const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
	const [isContextMenu, setIsContextMenu] = useState(false);
	const [startPoint, setStartPoint] = useState(null);

	useMapEvents({
		contextmenu: (e) => {
			setMousePosition({ x: e.layerPoint.x, y: e.layerPoint.y });
			setIsContextMenu(true);
			setStartPoint([e.latlng.lat, e.latlng.lng]);
		},
		click: () => {
			setIsContextMenu(false);
		},
	});

	return (
		<Pane name='nav-start' style={{ zIndex: 1000 }}>
			<div
				className={`context-menu ${isContextMenu ? 'active' : null}`}
				style={{ left: mousePosition.x + 10, top: mousePosition.y + 10 }}
				onClick={() => {
					setUserNavStart(startPoint);
				}}>
				{translation('map.setNavigationPoint')}
			</div>
		</Pane>
	);
};

const MapArea = ({ isPrint, floor, onPrintReady }) => {
	const { basicInfo, floorInfo, exhibitorInfo, recommendedRoutes } = jsonData.value;
	const { activeFloor, selectedBooth, navigation, currentNavStart, activeRoute } = appState.value;
	const { openRecommendedRoutes: showRecommendedRoutes } = configData.value;
	const [mapCurrent, setMapCurrent] = useState(null);
	const [baseImageLoaded, setBaseImageLoaded] = useState(false);
	const [mapFitted, setMapFitted] = useState(false);
	const hasReportedPrintReadyRef = useRef(false);

	const isPrintLandscape = isPrint && new URLSearchParams(window.location.search).get('orientation') === 'landscape';

	const onMapLoad = useCallback((map) => {
		setMapCurrent(map);
	}, []);

	const handleBaseImageLoad = useCallback(() => {
		setBaseImageLoaded(true);
	}, []);

	const currentFloor = useMemo(() => {
		if (isPrint && floor) {
			return floor;
		}
		return floorInfo ? floorInfo[activeFloor] : null;
	}, [activeFloor, floor, floorInfo, isPrint]);

	const currentMap = useMemo(() => {
		if (!currentFloor || !currentFloor.map) return {};
		return currentFloor.map;
	}, [currentFloor]);

	useEffect(() => {
		setBaseImageLoaded(false);
		setMapFitted(false);
		hasReportedPrintReadyRef.current = false;
	}, [currentMap.url, isPrint]);

	const bounds = useMemo(() => {
		if (!currentMap || !currentMap.width || !currentMap.height || !mapCurrent) {
			return null;
		}

		const A4WidthInPixels = 2200;
		const height = (currentMap.height / currentMap.width) * A4WidthInPixels;
		const southWest = mapCurrent.unproject([0, height], 2.5);
		const northEast = mapCurrent.unproject([A4WidthInPixels, 0], 2.5);
		const bounds = new latLngBounds(southWest, northEast);

		return bounds;
	}, [currentMap, mapCurrent]);

	useEffect(() => {
		if (mapCurrent && bounds && !selectedBooth) {
			if (isPrintLandscape) {
				setTimeout(() => {
					mapCurrent.invalidateSize();
					mapCurrent.fitBounds(bounds, { padding: [0, 0] });
					setMapFitted(true);
				}, 500);
			} else {
				mapCurrent.fitBounds(bounds);
				setMapFitted(true);
			}
			setTimeout(() => {
				const newBounds = new latLngBounds(bounds._southWest, bounds._northEast);
				newBounds._northEast.lat += (newBounds._northEast.lat - newBounds._southWest.lat) * 0.15;
				newBounds._northEast.lng += (newBounds._northEast.lng - newBounds._southWest.lng) * 0.15;
				newBounds._southWest.lat -= (newBounds._northEast.lat - newBounds._southWest.lat) * 0.15;
				newBounds._southWest.lng -= (newBounds._northEast.lng - newBounds._southWest.lng) * 0.15;

				mapCurrent.setMaxBounds(newBounds);
			}, 300);
		}
	}, [bounds, selectedBooth, mapCurrent, isPrintLandscape]);

	useEffect(() => {
		if (!isPrint || !mapCurrent || !bounds || !baseImageLoaded || !mapFitted || hasReportedPrintReadyRef.current) return;

		hasReportedPrintReadyRef.current = true;

		const notifyReady = async () => {
			mapCurrent.invalidateSize();
			mapCurrent.fitBounds(bounds, isPrintLandscape ? { padding: [0, 0] } : undefined);
			await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
			onPrintReady?.();
		};

		notifyReady();
	}, [baseImageLoaded, bounds, isPrint, isPrintLandscape, mapCurrent, mapFitted, onPrintReady]);

	useEffect(() => {
		if (!isPrint || !mapCurrent || !bounds) return;
		const refit = () => {
			mapCurrent.invalidateSize();
			mapCurrent.fitBounds(bounds, isPrintLandscape ? { padding: [0, 0] } : undefined);
		};
		window.addEventListener('beforeprint', refit);
		return () => window.removeEventListener('beforeprint', refit);
	}, [isPrint, isPrintLandscape, mapCurrent, bounds]);

	const currentFloorBooths = useMemo(() => {
		if (!currentFloor || !currentFloor.booths) return [];
		return currentFloor.booths.map((booth) => {
			const bounds = [
				[booth.bounds._northEast.lat, booth.bounds._northEast.lng],
				[booth.bounds._southWest.lat, booth.bounds._southWest.lng],
			];

			booth.bunds = bounds;

			return booth;
		});
	}, [currentFloor]);

	const currentFloorSections = useMemo(() => {
		return currentFloor?.sections || [];
	}, [currentFloor?.sections]);

	const currentFloorLinks = useMemo(() => {
		return currentFloor?.floorLinks || [];
	}, [currentFloor?.floorLinks]);

	const onlyFavourites = useMemo(() => {
		return exhibitorInfo.filter((exhibitor) => exhibitor.favourite);
	}, [exhibitorInfo]);

	const currentFloorBoothsWithFavourites = useMemo(() => {
		return currentFloorBooths.map((booth) => {
			const boothNumbers = boothNumbersOf(booth);
			const favourite = onlyFavourites.find((exhibitor) => boothNumbers.includes(exhibitor['攤位號碼']));
			return {
				...booth,
				favourite: !!favourite,
			};
		});
	}, [currentFloorBooths, onlyFavourites]);

	const onlyFavouriteBooths = useMemo(() => {
		return currentFloorBoothsWithFavourites.filter((booth) => booth.favourite);
	}, [currentFloorBoothsWithFavourites]);

	const { width } = useWindowSize();

	const minZoom = useMemo(() => {
		if (width <= 1440) return 1;
		return 2;
	}, [width]);

	const [startPoint, setStartPoint] = useState(null);
	const [endPoint, setEndPoint] = useState(null);

	const zoomAndPanToSelectedBooth = useCallback(() => {
		if (selectedBooth) {
			const booth = currentFloorBooths.find((booth) => boothHasNumber(booth, selectedBooth));
			if (booth) {
				const bounds = [
					[booth.bounds._northEast.lat, booth.bounds._northEast.lng],
					[booth.bounds._southWest.lat, booth.bounds._southWest.lng],
				];

				const center = [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2];

				if (navigation && startPoint) {
					mapCurrent?.setView(startPoint, minZoom + 1, true);
					setTimeout(() => {
						mapCurrent?.setView(startPoint, minZoom);
						// appState.value = {
						// 	...appState.value,
						// 	loading: false,
						// };
					}, 500);
				}

				if (!navigation) {
					mapCurrent?.setView(center, 3);
				}
			}
		}
	}, [currentFloorBooths, selectedBooth, navigation, minZoom, startPoint, mapCurrent]);

	useEffect(() => {
		zoomAndPanToSelectedBooth();
	}, [selectedBooth, zoomAndPanToSelectedBooth]);

	const [navigationPath, setNavigationPath] = useState(null);
	const [navigationDirection, setNavigationDirection] = useState(null);
	const [userNavStart, setUserNavStart] = useState(null);

	const roundedPaths = useMemo(() => {
		const paths = currentFloor?.paths;
		if (!Array.isArray(paths)) return [];

		// Bresenham line: enumerate all integer points between two endpoints
		const bresenham = (x0, y0, x1, y1) => {
			const points = [];
			let dx = Math.abs(x1 - x0);
			let dy = Math.abs(y1 - y0);
			let sx = x0 < x1 ? 1 : -1;
			let sy = y0 < y1 ? 1 : -1;
			let err = dx - dy;
			let cx = x0;
			let cy = y0;

			while (true) {
				points.push([cx, cy]);
				if (cx === x1 && cy === y1) break;
				const e2 = 2 * err;
				if (e2 > -dy) {
					err -= dy;
					cx += sx;
				}
				if (e2 < dx) {
					err += dx;
					cy += sy;
				}
			}
			return points;
		};

		return paths
			.filter((path) => Array.isArray(path?.positions) && path.positions.length >= 2 && path.positions.every((pos) => Array.isArray(pos) && pos.length >= 2 && Number.isFinite(pos[0]) && Number.isFinite(pos[1])))
			.map((path) => {
				const roundedPositions = path.positions.map((position) => {
					return [Math.round(position[0]), Math.round(position[1])];
				});

				const allPoints = [];

				// Process each consecutive pair (supports multi-segment paths)
				for (let seg = 0; seg < roundedPositions.length - 1; seg++) {
					const p0 = roundedPositions[seg];
					const p1 = roundedPositions[seg + 1];
					const segPoints = bresenham(p0[0], p0[1], p1[0], p1[1]);
					// Skip first point of subsequent segments to avoid duplicates at junctions
					const start = seg > 0 ? 1 : 0;
					for (let i = start; i < segPoints.length; i++) {
						allPoints.push(segPoints[i]);
					}
				}

				return {
					...path,
					positions: roundedPositions,
					allPoints,
				};
			});
	}, [currentFloor?.paths]);

	useEffect(() => {
		if (!navigation && userNavStart) {
			const start = [Math.round(userNavStart[0]), Math.round(userNavStart[1])];
			let closestDistanceToStart = null;
			let closestPointToStart = null;
			roundedPaths.forEach((path) => {
				path.allPoints.forEach((point) => {
					const distance = Math.sqrt(Math.pow(point[0] - start[0], 2) + Math.pow(point[1] - start[1], 2));
					if (!closestDistanceToStart || distance < closestDistanceToStart) {
						closestDistanceToStart = distance;
						closestPointToStart = point;
					}
				});
			});
			setStartPoint(closestPointToStart);
		}
	}, [selectedBooth, navigation, userNavStart, roundedPaths]);

	useEffect(() => {
		if (!navigation && currentNavStart) {
			const start = [Number(currentNavStart['X軸']), Number(currentNavStart['Y軸'])];
			let closestDistanceToStart = null;
			let closestPointToStart = null;
			roundedPaths?.forEach((path) => {
				path.allPoints.forEach((point) => {
					const distance = Math.sqrt(Math.pow(point[0] - start[0], 2) + Math.pow(point[1] - start[1], 2));
					if (!closestDistanceToStart || distance < closestDistanceToStart) {
						closestDistanceToStart = distance;
						closestPointToStart = point;
					}
				});
			});
			setStartPoint(closestPointToStart);
		}
	}, [selectedBooth, navigation, currentNavStart, roundedPaths]);

	useEffect(() => {
		if (selectedBooth && navigation) {
			const booth = currentFloorBooths.find((booth) => boothHasNumber(booth, selectedBooth));

			// 目標攤位不在這一層——使用者沒按結束導航就換了樓層。
			// 這時 booth 是 undefined，底下的終點會算成 NaN。findPath 挑最近點的條件是
			// `!closestDistanceToEnd || distance < closestDistanceToEnd`，而 !NaN 是 true，
			// 於是每一圈都成立、一路覆蓋到路徑的最後一個點——導航線就指到那裡。
			// 做法跟按「結束導航」完全一樣：把 navigation 關掉，
			// 導航線的那個 Pane 外層就是 navigation 判斷，線會自己收掉。
			// currentFloorBooths 為空代表樓層資料還沒載完（例如切語言時重抓），
			// 那不是換樓層，不要在那時候把導航關掉。
			if (currentFloorBooths.length > 0 && !booth) {
				appState.value = {
					...appState.value,
					navigation: false,
				};
				return;
			}

			const urlParams = new URLSearchParams(window.location.search);
			const urlFloor = urlParams.get('floor');
			const urlStart = urlParams.get('start');

			let lat;
			let lng;

			if (!urlFloor || !urlStart || Number(urlFloor) !== currentFloor?.floor) {
				if (currentNavStart) {
					lat = Number(currentNavStart['X軸']);
					lng = Number(currentNavStart['Y軸']);
				}
			}

			if (urlStart && Number(urlFloor) === currentFloor.floor) {
				const start = urlStart.split(',');
				lat = Number(start[0]);
				lng = Number(start[1]);
			}

			if (userNavStart) {
				lat = userNavStart[0];
				lng = userNavStart[1];
			}

			if (!lat || !lng) return;

			const startCoord = [lat, lng];
			const bounds = [
				[booth?.bounds._northEast.lat, booth?.bounds._northEast.lng],
				[booth?.bounds._southWest.lat, booth?.bounds._southWest.lng],
			];
			const endCoord = [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2];

			const result = findPath(roundedPaths, startCoord, endCoord);

			if (result) {
				setStartPoint(result.startPoint);
				setEndPoint(result.endPoint);
				setNavigationDirection(result.direction);
				setNavigationPath(result.path);
			} else {
				setNavigationPath(null);
			}
		}
	}, [currentFloor, currentFloorBooths, currentNavStart, navigation, roundedPaths, selectedBooth, userNavStart]);

	const markerEnd = useMemo(() => {
		if (navigationDirection === 'left') return markerLeft;
		if (navigationDirection === 'right') return markerRight;
		if (navigationDirection === 'up') return markerUp;
		if (navigationDirection === 'down') return markerDown;
		return null;
	}, [navigationDirection]);

	const endNavigation = () => {
		appState.value = {
			...appState.value,
			navigation: false,
		};
	};

	// === Recommended Route State ===
	const [routePath, setRoutePath] = useState(null);
	const [routeStopMarkers, setRouteStopMarkers] = useState([]);

	// Group and resolve route stops
	const resolvedRouteStops = useMemo(() => {
		if (!activeRoute || !recommendedRoutes || !recommendedRoutes.length) return [];

		const rows = recommendedRoutes.filter((r) => r['路線ID'] === activeRoute);
		if (!rows.length) return [];

		let boothIndex = 0;
		return rows
			.sort((a, b) => Number(a['站序']) - Number(b['站序']))
			.map((stop) => {
				const isBooth = stop['點位類型'] === '攤位';
				const isPassage = stop['點位類型'] === '樓層通道';
				let center = null;
				let floorIndex = -1;

				if (isBooth) {
					boothIndex++;
					const boothNumber = stop['攤位號碼'];
					floorInfo.forEach((floor, index) => {
						if (floor.booths) {
							const found = floor.booths.find((b) => boothHasNumber(b, boothNumber));
							if (found) {
								floorIndex = index;
								const bounds = found.bounds;
								center = [(bounds._northEast.lat + bounds._southWest.lat) / 2, (bounds._northEast.lng + bounds._southWest.lng) / 2];
							}
						}
					});
				}

				if (isPassage && stop['X軸'] && stop['Y軸'] && stop['樓層']) {
					center = [Number(stop['X軸']), Number(stop['Y軸'])];
					floorInfo.forEach((floor, index) => {
						if (String(floor.floor) === String(stop['樓層'])) floorIndex = index;
					});
				}

				return {
					...stop,
					isBooth,
					isPassage,
					center,
					floorIndex,
					boothLabel: isBooth ? String.fromCharCode(64 + boothIndex) : null,
				};
			})
			.filter((s) => s.center !== null);
	}, [activeRoute, recommendedRoutes, floorInfo]);

	// Current floor stops for rendering
	const currentFloorRouteStops = useMemo(() => {
		return resolvedRouteStops.filter((s) => s.floorIndex === activeFloor);
	}, [resolvedRouteStops, activeFloor]);

	// Compute multi-stop route for current floor
	// Pass ALL stops to computeMultiStopRoute so it respects original order,
	// then only draw segments where both endpoints are on the current floor.
	useEffect(() => {
		if (!activeRoute || currentFloorRouteStops.length === 0) {
			setRoutePath(null);
			setRouteStopMarkers(currentFloorRouteStops);
			return;
		}

		const result = computeMultiStopRoute(roundedPaths, resolvedRouteStops, activeFloor);
		setRoutePath(result.allPoints.length > 0 ? result.allPoints : null);
		setRouteStopMarkers(currentFloorRouteStops);
	}, [activeRoute, currentFloorRouteStops, resolvedRouteStops, activeFloor, roundedPaths]);

	// Clear route when activeRoute is null
	useEffect(() => {
		if (!activeRoute) {
			setRoutePath(null);
			setRouteStopMarkers([]);
		}
	}, [activeRoute]);

	const cancelRoute = () => {
		appState.value = {
			...appState.value,
			activeRoute: null,
		};
	};

	const openRecommendedRoutes = () => {
		appState.value = {
			...appState.value,
			RecommendedRoutes: true,
		};
	};

	const handlePassageMarkerClick = (stop) => {
		if (stop.floorIndex >= 0 && stop.floorIndex !== activeFloor) {
			appState.value = { ...appState.value, activeFloor: stop.floorIndex };
		}
		if (stop.center && mapCurrent) {
			mapCurrent.setView(stop.center, mapCurrent.getZoom());
		}
	};

	// Create lettered marker icon for booth stops
	const createLetterMarkerIcon = useCallback(
		(letter) => {
			const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><circle cx="16" cy="16" r="14" fill="#E86D00" stroke="white" stroke-width="2"/><text x="16" y="22" text-anchor="middle" fill="white" font-size="16" font-weight="bold" font-family="Noto Sans TC">${letter}</text></svg>`;
			const encoded = encodeURIComponent(svg);
			return icon({
				iconUrl: `data:image/svg+xml,${encoded}`,
				iconSize: [32, 32],
				iconAnchor: [16, 16],
			});
		},
		[]
	);

	// Create stairs marker icon for floor passage stops
	const stairsMarkerIcon = useMemo(() => {
		const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><circle cx="16" cy="16" r="14" fill="#667085" stroke="white" stroke-width="2"/><path d="M10 22h4v-4h4v-4h4v-4" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
		const encoded = encodeURIComponent(svg);
		return icon({
			iconUrl: `data:image/svg+xml,${encoded}`,
			iconSize: [32, 32],
			iconAnchor: [16, 16],
		});
	}, []);

	if (!currentFloor || !basicInfo || !floorInfo || !exhibitorInfo) return null;

	return (
		<div id='MapArea' className='MapArea'>
			<MapContainer center={[0, 0]} zoom={isPrint ? 1 : minZoom} maxZoom={isPrintLandscape ? 5 : isPrint ? 1 : 5} minZoom={isPrintLandscape ? 0 : isPrint ? 1 : minZoom} zoomSnap={isPrintLandscape ? 0 : 1} ref={onMapLoad} crs={CRS.Simple} {...(isPrint ? { dragging: false, touchZoom: false, doubleClickZoom: false, scrollWheelZoom: false, boxZoom: false, keyboard: false, zoomControl: false } : {})}>
				<MapPane bounds={bounds} currentMap={currentMap} onImageLoad={handleBaseImageLoad} />

				<NavStartPane setUserNavStart={setUserNavStart} />

				<Pane name='sections' style={{ zIndex: 200 }}>
					{currentFloorSections.map((section) => {
						let opacity = 1;

						if (section.opacity) {
							const numOpacity = Number(section.opacity);

							if (numOpacity > 0 && numOpacity <= 100) {
								opacity = numOpacity / 100;
							}

							if (numOpacity === 0) {
								opacity = 0;
							}
						}

						if (section.layerType === 'rectangle') {
							const bounds = [
								[section.bounds._northEast.lat, section.bounds._northEast.lng],
								[section.bounds._southWest.lat, section.bounds._southWest.lng],
							];

							return (
								<Rectangle
									key={section.id}
									bounds={bounds}
									pathOptions={{
										fillColor: section.fillColor,
										fillOpacity: opacity,
										weight: 0,
									}}
								/>
							);
						}

						if (section.layerType === 'polygon') {
							return (
								<Polygon
									key={section.id}
									positions={section.positions}
									pathOptions={{
										fillColor: section.fillColor,
										fillOpacity: opacity,
										weight: 0,
									}}
								/>
							);
						}

						return null;
					})}
				</Pane>

				{/* Floor Links clickable overlay */}
				<Pane name='floor-links' style={{ zIndex: 350 }}>
					{currentFloorLinks.map((link) => {
						let opacity = 0.3;
						if (link.opacity !== undefined && link.opacity !== null) {
							const num = Number(link.opacity);
							if (!Number.isNaN(num)) opacity = Math.max(0, Math.min(1, num > 1 ? num / 100 : num));
						}

						const fillColor = link.color || '#ff9800';
						const pathOptions = { className: 'floor-link', fillColor, fillOpacity: opacity, stroke: false, weight: 0 };

						const onClick = () => {
							if (!link.linkFloor && link.linkFloor !== 0) return;
							const toFloor = Number(link.linkFloor);
							if (Number.isNaN(toFloor)) return;

							// update app state
							appState.value = {
								...appState.value,
								activeFloor: toFloor,
								selectedBooth: null,
								navigation: false,
							};

							// update URL param
							const params = new URLSearchParams(window.location.search);
							params.set('floor', String(toFloor));
							const newUrl = `${window.location.pathname}?${params.toString()}`;
							window.history.replaceState({}, '', newUrl);
						};

						if (link.layerType === 'rectangle' && link.bounds) {
							const b = [
								[link.bounds._northEast.lat, link.bounds._northEast.lng],
								[link.bounds._southWest.lat, link.bounds._southWest.lng],
							];
							return <Rectangle key={link.id} bounds={b} pathOptions={pathOptions} eventHandlers={{ click: onClick }} className='floorLink' />;
						}

						if (link.layerType === 'polygon' && Array.isArray(link.positions)) {
							return <Polygon key={link.id} positions={link.positions} pathOptions={pathOptions} eventHandlers={{ click: onClick }} className='floorLink' />;
						}

						return null;
					})}
				</Pane>

				<Pane name='booths' style={{ zIndex: 300 }}>
					{currentFloorBooths
						? currentFloorBooths.map((booth) => {
								const exhibitors = boothExhibitorsOf(booth, exhibitorInfo);
								return <FeatureWithText map={mapCurrent} shape={booth.layerType} key={booth.id} id={booth.id} bounds={booth.bunds} positions={booth.positions} exhibitors={exhibitors} className='booth' colors={basicInfo.boothColor} isPrint={isPrint} />;
							})
						: null}
				</Pane>

				{navigation ? (
					<Pane name='navigation' style={{ zIndex: 250 }}>
						{navigationPath ? (
							<Polyline
								positions={navigationPath}
								pathOptions={{
									color: '#E86D00',
									opacity: 1,
									weight: 3,
								}}
								interactive={false}
							/>
						) : null}
					</Pane>
				) : null}

				{startPoint ? <NavMarker center={startPoint} marker={markerStart} /> : null}

				{navigation && endPoint && markerEnd ? <NavMarker center={endPoint} marker={markerEnd} /> : null}

				{/* Recommended Route Polyline (rendered in overlayPane so markers stay on top) */}
				{activeRoute && routePath ? (
					<Pane name='route-line' style={{ zIndex: 250 }}>
						<Polyline
							positions={routePath}
							pathOptions={{
								color: '#E86D00',
								opacity: 1,
								weight: 3,
							}}
							interactive={false}
						/>
					</Pane>
				) : null}

				{/* Recommended Route Markers */}
				{activeRoute && routeStopMarkers.length > 0
					? routeStopMarkers.map((stop, i) => {
							if (stop.isBooth) {
								return (
									<Marker
										key={`route-booth-${stop['站序']}`}
										position={{ lat: stop.center[0], lng: stop.center[1] }}
										icon={createLetterMarkerIcon(stop.boothLabel)}
										zIndexOffset={1000}
										eventHandlers={{
											click: () => {
												appState.value = {
													...appState.value,
													selectedBooth: stop['攤位號碼'],
													selectedExhibitor: exhibitorKeyOf(firstExhibitorAtBooth(exhibitorInfo, stop['攤位號碼'])),
													ExhibitorInfo: true,
												};
											},
										}}
									/>
								);
							}
							if (stop.isPassage) {
								return (
									<Marker
										key={`route-passage-${stop['站序']}`}
										position={{ lat: stop.center[0], lng: stop.center[1] }}
										icon={stairsMarkerIcon}
										zIndexOffset={1000}
										eventHandlers={{ click: () => handlePassageMarkerClick(stop) }}
									/>
								);
							}
							return null;
						})
					: null}

				{isPrint
					? onlyFavouriteBooths.map((booth) => {
							const bounds = [
								[booth.bounds._northEast.lat, booth.bounds._northEast.lng],
								[booth.bounds._southWest.lat, booth.bounds._southWest.lng],
							];

							return <FavMarker key={booth.id} bounds={bounds} marker={markerFav} />;
						})
					: null}
			</MapContainer>

			{navigation ? (
				<div onClick={endNavigation} className='end-navigation'>
					<img src={endNav} alt='' />
				</div>
			) : null}

			{!isPrint && activeRoute ? (
				<div onClick={cancelRoute} className='end-navigation'>
					<img src={endNav} alt='' />
				</div>
			) : null}

			{showRecommendedRoutes && !isPrint && !navigation && !activeRoute ? (
				<div onClick={openRecommendedRoutes} className='route-shortcut'>
					<svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
						<path d='M9 20L3 17V4L9 7M9 20L15 17M9 20V7M15 17L21 20V7L15 4M15 17V4M9 7L15 4' stroke='#344054' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
					</svg>
				</div>
			) : null}

		</div>
	);
};

export default MapArea;
